import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { HERO, IMAGES } from "../config/content";

const EASE = [0.22, 1, 0.36, 1];

function CandleBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-[0.05] candle-drift" aria-hidden="true" preserveAspectRatio="none" viewBox="0 0 1200 600">
      <polyline points="0,420 120,380 240,430 360,330 480,370 600,280 720,320 840,230 960,270 1080,180 1200,220" fill="none" stroke="#22C55E" strokeWidth="2" />
      {Array.from({ length: 24 }).map((_, i) => {
        const x = 40 + i * 48;
        const up = i % 3 !== 0;
        const y = 120 + ((i * 67) % 320);
        const h = 30 + ((i * 37) % 60);
        return (
          <g key={i} stroke={up ? "#22C55E" : "#EF4444"} strokeWidth="2">
            <line x1={x} y1={y - 12} x2={x} y2={y + h + 12} />
            <rect x={x - 6} y={y} width="12" height={h} fill={up ? "#22C55E" : "#EF4444"} stroke="none" />
          </g>
        );
      })}
    </svg>
  );
}

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-[270px] sm:w-[300px]" data-testid="hero-phone-mockup">
      <div className="absolute -inset-8 bg-[#22C55E]/15 blur-3xl rounded-full" aria-hidden="true" />
      <div className="relative rounded-[2.4rem] border border-[#22C55E]/30 bg-[#0F1712] p-2.5 shadow-[0_30px_80px_-20px_rgba(34,197,94,0.3)]">
        <div className="relative rounded-[1.9rem] overflow-hidden bg-black aspect-[9/19]">
          {IMAGES.heroVideo ? (
            <video src={IMAGES.heroVideo} poster={IMAGES.heroPoster} autoPlay muted loop playsInline className="w-full h-full object-cover" />
          ) : (
            <div className="relative w-full h-full">
              <img src={IMAGES.heroPoster} alt="Live trading session placeholder" className="w-full h-full object-cover opacity-70" loading="eager" />
              <CandleMini />
            </div>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-1.5 rounded-full border border-[#22C55E]/40">
            <span className="w-2 h-2 rounded-full bg-[#22C55E] blink" aria-hidden="true" />
            <span className="font-heading text-[11px] tracking-[0.2em] text-white">{HERO.liveTag}</span>
          </div>
          <div className="absolute bottom-3 inset-x-3 bg-black/70 backdrop-blur rounded-xl px-3 py-2 border border-[#17231B]">
            <p className="text-[11px] text-[#A7B3AA]">Live market analysis with mentors</p>
            {!IMAGES.heroVideo && <p className="text-[10px] text-[#6B7A70]">[Your session video goes here]</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function CandleMini() {
  return (
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 620" preserveAspectRatio="none" aria-hidden="true">
      <polyline points="0,500 50,460 100,490 150,400 200,430 250,340 300,370" fill="none" stroke="#22C55E" strokeWidth="2" opacity="0.8" />
      {Array.from({ length: 9 }).map((_, i) => {
        const x = 30 + i * 32;
        const up = i % 3 !== 1;
        const y = 220 + ((i * 53) % 200);
        const h = 24 + ((i * 29) % 40);
        return (
          <g key={i} opacity="0.85">
            <line x1={x} y1={y - 10} x2={x} y2={y + h + 10} stroke={up ? "#22C55E" : "#EF4444"} strokeWidth="1.5" />
            <rect x={x - 5} y={y} width="10" height={h} fill={up ? "#22C55E" : "#EF4444"} />
          </g>
        );
      })}
    </svg>
  );
}

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24" data-testid="hero-section">
      <CandleBackground />
      <motion.div style={{ y: yGlow }} className="absolute top-10 left-1/2 -translate-x-1/2 w-[700px] h-[420px] bg-[#22C55E]/[0.13] blur-[120px] rounded-full pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-[#FACC15]/30 bg-[#FACC15]/[0.07] px-4 py-1.5 mb-7"
            data-testid="hero-tag"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FACC15]" aria-hidden="true" />
            <span className="text-xs font-semibold tracking-wide text-[#FACC15] uppercase">{HERO.tag}</span>
          </motion.div>

          <h1 className="font-heading uppercase text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide" data-testid="hero-heading">
            <MaskedLine delay={0.1}>{HERO.titleA}</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-[#22C55E] glow-text-green">{HERO.titleHighlight}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg text-[#A7B3AA] leading-relaxed max-w-xl"
            data-testid="hero-sub"
          >
            {HERO.sub}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            className="mt-6 space-y-2.5"
            data-testid="hero-ticks"
          >
            {HERO.ticks.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm sm:text-base text-[#A7B3AA]">
                <Check className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" strokeWidth={3} />
                {t}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            className="mt-8"
          >
            <button
              onClick={onBook}
              data-testid="hero-book-button"
              className="btn-primary group relative inline-flex items-center gap-3 rounded-full bg-[#22C55E] hover:bg-[#16A34A] text-[#04100A] font-extrabold text-base sm:text-lg px-8 py-4 transition-colors overflow-hidden"
            >
              <span className="relative z-10">{HERO.cta}</span>
              <ArrowRight className="w-5 h-5 relative z-10 arrow-slide" />
              <span className="shine" aria-hidden="true" />
            </button>
            <p className="mt-4 text-xs sm:text-sm text-[#6B7A70] max-w-md" data-testid="hero-small-text">{HERO.smallText}</p>
            <p className="mt-4 text-sm font-semibold text-[#FACC15]" data-testid="hero-trust-line">{HERO.trust}</p>
          </motion.div>
        </div>

        <motion.div style={{ y: yPhone }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}>
          <PhoneMockup />
        </motion.div>
      </div>
    </section>
  );
}
